import { DownloadOutlined, ReloadOutlined, SoundOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/button/button';
import { getRecordingUrl, RecordingCategory } from '@/services/conversations-service/conversations-service';
import { RecordingStatus } from '@/services/conversations-service/conversations-service.types';
import styles from './recording-row.module.scss';

type RecordingRowProps = {
  label: string;
  url: string | undefined;
  status: RecordingStatus | undefined;
  onRefresh: () => void;
  // Required to fetch a fresh download-flavoured URL when the user clicks
  // Download. Without these, the row falls back to using the playback url
  // directly (works cross-origin for browsers that respect the `download`
  // attribute, but the saved filename will be Twilio's RecordingSid).
  conversationId?: number;
  category?: RecordingCategory;
};

// Renders one row of the recording strip with three terminal states. The
// 'processing' state shows a friendly message + a manual refresh button (the
// hook also auto-polls every 15s but admins like the option). 'unavailable'
// surfaces a muted message instead of silently hiding the row, since the
// previous behavior made admins assume the recording feature was broken
// when in reality the call had ended too long ago for Twilio to deliver.
export function RecordingRow({ label, url, status, onRefresh, conversationId, category }: RecordingRowProps) {
  const { t } = useTranslation(['webchat', 'common']);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!url) return;
    // Prefer fetching a fresh URL with download=true so the backend can
    // attach Content-Disposition: attachment + a sensible filename. Without
    // conversationId/category we fall back to the playback URL — better
    // than nothing, and modern browsers usually still trigger a save.
    let target = url;
    if (conversationId !== undefined) {
      setDownloading(true);
      try {
        const result = await getRecordingUrl(conversationId, category, { download: true });
        if (result?.url) target = result.url;
      } catch {
        // Fall through to the playback url; user still gets *something*.
      } finally {
        setDownloading(false);
      }
    }
    triggerBrowserDownload(target);
  };

  if (status === 'ready' && url) {
    return (
      <div className={styles.recordingRow}>
        <SoundOutlined className={styles.icon} />
        <span className={styles.label}>{label}</span>
        {/* controlsList="nodownload" hides the native download icon so admins
            don't have two download paths — the native one (saves under
            Twilio's RecordingSid or the S3 key, neither readable) and our
            labeled button (saves with respondit-conversation-{id}-{cat}.mp3
            via Content-Disposition). One mechanism, predictable filename. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio controls controlsList="nodownload" src={url} className={styles.player} />
        <Button
          size="small"
          type="text"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          loading={downloading}
          aria-label={t('webchat:recordingRow.downloadAria', { label })}
        >
          {t('common:actions.download')}
        </Button>
      </div>
    );
  }
  if (status === 'processing') {
    return (
      <div className={styles.recordingRow} role="status" aria-live="polite">
        <SoundOutlined className={styles.icon} />
        <span className={styles.label}>{label}</span>
        <span className={styles.muted}>{t('webchat:recordingRow.processing')}</span>
        <Button size="small" type="text" icon={<ReloadOutlined />} onClick={onRefresh}>
          {t('webchat:recordingRow.refresh')}
        </Button>
      </div>
    );
  }
  if (status === 'unavailable') {
    return (
      <div className={styles.recordingRow}>
        <SoundOutlined className={`${styles.icon} ${styles.muted}`} />
        <span className={`${styles.label} ${styles.muted}`}>{label}</span>
        <span className={styles.muted}>{t('webchat:recordingRow.unavailable')}</span>
      </div>
    );
  }
  // status undefined (initial load) — render nothing so the panel doesn't
  // jump as the query resolves.
  return null;
}

// Standard "trigger a browser download from JS" pattern. Uses a hidden
// anchor + click so the current tab isn't navigated away. The `download`
// attribute is a hint for same-origin URLs and ignored cross-origin, which
// is fine — for cross-origin (Twilio/S3) we rely on the server's
// Content-Disposition to control filename and save behavior.
function triggerBrowserDownload(href: string) {
  const a = document.createElement('a');
  a.href = href;
  a.rel = 'noopener noreferrer';
  a.download = '';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
